import { useMemo, useState } from "react";

import { Icon } from "@/components";
import {
    HeaderActions,
    Heading,
    Panel,
    Tabs,
    Text,
    type HeaderActionItem,
} from "@/ui";
import { useMeta, useNotification } from "@/hooks";
import { Order, Account, IdpAccount } from "@/types";
import { Content } from "@/layout";
import { useOrderLineItemProductNames } from "@/opensearch";

import { useQuery } from "@apollo/react-hooks";
import { QUERY_GET_ORDER } from "../../apollo/module-operations/order";
import { QUERY_GET_STORE } from "../../apollo/module-operations/store";
import {
    QUERY_GET_CUSTOMER_BY_EMAIL,
    QUERY_GET_IDP_ACCOUNT,
} from "apollo/module-operations/customer";

import { printInvoice, printSlip } from "./print";

import OrderDataPane from "./kanban/panes/OrderDataPane";
import AdditionalDataPane from "./kanban/panes/AdditionalDataPane";
import ActivityLogPane from "./kanban/panes/ActivityLogPane";
import RawPane from "./kanban/panes/RawPane";

import _ from "lodash";
import { useOrderStatusActions } from "./OrderActionButtons";
import styles from "./OrderForm.module.scss";

const PrintIcon = () => <Icon icon="print" />;
const ReceiptIcon = () => <Icon icon="receipt" />;
const RefreshIcon = () => <Icon icon="refresh" />;

const ORDER_TABS = {
    data: "order-data",
    additional: "additional-data",
    activity: "activity-logs",
    raw: "raw",
} as const;

const OrderForm = ({ algoliaOrder, isModal = false }) => {
    const { user, resourceId, isCreate } = useMeta();
    const [storeInformation, setStoreInformation] = useState<object>();
    const [order, setOrder] = useState<Order>();
    const [account, setAccount] = useState<Account>();
    const [ipdAccount, setIDPAccount] = useState<IdpAccount>();
    const [activeTab, setActiveTab] = useState<string>(ORDER_TABS.data);

    const variables = {
        account_id: user.accountId,
        store_id: user.storeId,
    };

    const entityId = isModal ? algoliaOrder.objectID : resourceId;

    const { refetch, loading: getOrderLoading } = useQuery(QUERY_GET_ORDER, {
        skip: user.loading || !entityId || !user.accountId || !user.storeId,
        fetchPolicy: "network-only",
        notifyOnNetworkStatusChange: true,
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
            entity_id: entityId,
        },
        onCompleted: resp => {
            const order = (resp && resp.getOrder) || {};

            setOrder(order);
        },
    });

    const lineItemPosIds = useMemo(
        () =>
            order?.line_items
                ?.map(item => item.pos_id)
                .filter((id): id is string => Boolean(id)) ?? [],
        [order?.line_items],
    );

    const {
        namesProductsPOS,
        isLoading: searchProductNamesLoading,
        statusMessage: productNamesStatusMessage,
    } = useOrderLineItemProductNames({
        providerStoreID: order?.jane_store_id,
        posIds: lineItemPosIds,
        enabled: !user.loading && !getOrderLoading && Boolean(order),
    });

    const { loading: getStoreLoading } = useQuery(QUERY_GET_STORE, {
        variables,
        skip: user.loading || !user.accountId || !user.storeId,
        onCompleted: resp => {
            setStoreInformation(resp.getStore);
        },
    });

    const { loading: getAccoutLoading } = useQuery(
        QUERY_GET_CUSTOMER_BY_EMAIL,
        {
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                email: order?.email,
            },
            skip: user.loading || getOrderLoading || !order,
            onCompleted: resp => {
                setAccount(resp.getAccountByEmail);
            },
        },
    );

    const { loading: getIDPAccoutLoading } = useQuery(QUERY_GET_IDP_ACCOUNT, {
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
            email: order?.email,
        },
        skip: user.loading || getOrderLoading || !order,
        onCompleted: resp => {
            setIDPAccount(resp.getIDPAccount);
        },
        onError: () => {
            setIDPAccount(null);
        },
    });

    const notify = useNotification();

    const onPrint = (option: number) => {
        if (option === 1) {
            const pdf = printInvoice(
                [order],
                storeInformation,
                "country",
                "state",
            );
            pdf.open();
        } else if (option === 2) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const _order = _.cloneDeep(order) as any;

            _order.shippingPackages = _.map(order.line_items, p => ({
                id: p.id,
                title: p.name,
                qtyInPackage: p.quantity,
            }));

            const pdf = printSlip(
                [_order],
                storeInformation,
                "country",
                "state",
            );
            pdf.open();
        }
    };

    const statusActions = useOrderStatusActions({
        order: isModal ? undefined : order,
        onActionCompleted: refetch,
    });

    const headerActions: HeaderActionItem[] = [
        {
            id: "print",
            label: "Print",
            icon: <PrintIcon />,
            variant: "secondary",
            menuItems: [
                {
                    id: "invoice",
                    label: "Invoice",
                    icon: <ReceiptIcon />,
                    onSelect: () => onPrint(1),
                },
                {
                    id: "packaging",
                    label: "Packaging slips",
                    icon: <ReceiptIcon />,
                    onSelect: () => onPrint(2),
                },
            ],
        },
        ...(!isCreate
            ? [
                  {
                      id: "refresh",
                      label: "Refresh",
                      icon: <RefreshIcon />,
                      variant: "secondary" as const,
                      onClick: () => {
                          void refetch();
                      },
                  },
              ]
            : []),
        ...statusActions,
    ];

    const isLoading =
        getOrderLoading ||
        searchProductNamesLoading ||
        getStoreLoading ||
        getAccoutLoading ||
        getIDPAccoutLoading ||
        !order;

    const renderTabs = () => {
        return (
            <Tabs
                aria-label="Order detail sections"
                className={isModal ? styles.modalTabs : undefined}
                onValueChange={setActiveTab}
                value={activeTab}
            >
                <Tabs.List sticky={!isModal}>
                    <Tabs.Trigger value={ORDER_TABS.data}>
                        Order data
                    </Tabs.Trigger>
                    <Tabs.Trigger value={ORDER_TABS.additional}>
                        Additional data
                    </Tabs.Trigger>
                    <Tabs.Trigger value={ORDER_TABS.activity}>
                        Activity logs
                    </Tabs.Trigger>
                    <Tabs.Trigger value={ORDER_TABS.raw}>Raw</Tabs.Trigger>
                </Tabs.List>
                <Tabs.Content
                    className={isModal ? styles.modalTabPanel : undefined}
                    value={ORDER_TABS.data}
                >
                    <OrderDataPane
                        account={account}
                        idpAccount={ipdAccount}
                        loading={isLoading}
                        namesProductsPOS={namesProductsPOS}
                        order={order}
                        productNamesStatusMessage={productNamesStatusMessage}
                    />
                </Tabs.Content>
                <Tabs.Content
                    className={isModal ? styles.modalTabPanel : undefined}
                    value={ORDER_TABS.additional}
                >
                    <AdditionalDataPane loading={isLoading} order={order} />
                </Tabs.Content>
                <Tabs.Content
                    className={isModal ? styles.modalTabPanel : undefined}
                    value={ORDER_TABS.activity}
                >
                    <ActivityLogPane loading={isLoading} order={order} />
                </Tabs.Content>
                <Tabs.Content
                    className={isModal ? styles.modalTabPanel : undefined}
                    value={ORDER_TABS.raw}
                >
                    <RawPane loading={isLoading} order={order} />
                </Tabs.Content>
            </Tabs>
        );
    };

    return isModal ? (
        renderTabs()
    ) : (
        <Content>
            <Panel
                className={styles.root}
                header={
                    <div className={styles.headerBar}>
                        <div className={styles.headerText}>
                            <Heading level={4}>Order</Heading>
                            <Text tone="secondary" variant="body-md">
                                Order details
                            </Text>
                        </div>
                        <HeaderActions
                            actions={headerActions}
                            aria-label="Order actions"
                        />
                    </div>
                }
                showHeaderDivider
            >
                {renderTabs()}
            </Panel>
        </Content>
    );
};

export default OrderForm;
